import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import { Loading } from '@ui/Loading';

import type { Capability } from './capabilities';
import { can } from './entitlements';
import { useEntitlements } from './useCan';

/**
 * Renders a route only for a user holding `capability`, and answers 404
 * otherwise. Waits while sign-in is unresolved rather than bouncing on the
 * empty grant every user has for the first few hundred milliseconds.
 *
 * A convenience for rendering the right failure, not a security boundary.
 */
export function RequireCapability({ capability, children }: { capability: Capability; children: ReactNode }) {
  const entitlements = useEntitlements();

  if (entitlements.pending) {
    return <Loading variant="page" label="Checking access" />;
  }

  return can(entitlements, capability) ? <>{children}</> : <Navigate to="/404" replace />;
}
